import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
  ContactPhone as MobileIcon,
  Fingerprint as AadhaarIcon,
  AccountBox as AbhaAddressIcon,
  Numbers as AbhaNumberIcon,
  PlayArrow as UsePatientIcon,
  RecordVoiceOver as AbhaIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import useSWR, { mutate as globalMutate } from 'swr';
import { z } from 'zod';
import { abha, type AbhaProfile, type CheckRevisitResponse, type PatientProfile } from '@/api/clinical';
import { useAuth } from '@/auth/AuthContext';
import { DataTable } from '@/components/DataTable';
import { FieldRow } from '@/components/FieldRow';
import { Pagination } from '@/components/Pagination';
import { StatusPill } from '@/components/StatusPill';
import { Wizard, type WizardStep, type WizardStepController } from '@/components/Wizard';
import { useToast } from '@/components/Toaster';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';
import { activePatient, type ActivePatient } from '@/utils/activePatient';

/**
 * Patient list + ABHA wizard. Mirrors the M1 Connector API Documentation
 * v2.0 verification flow (sections 2.x — onboarding via Aadhaar OTP,
 * 4.x — verify via existing ABHA address / mobile / ABHA number).
 *
 * Single entry point — one wizard, four verification methods. Step 0
 * picks the method; the rest of the steps fan out from there. This
 * matches the legacy gj_clinic RegistrationForm posture: the user
 * always answers "how do you want to verify?" first, and the wizard
 * routes accordingly.
 *
 * Endpoints used (canonical paths from the doc):
 *   2.1  POST /api/abdm/v3/request/otp           — Aadhaar / Mobile OTP
 *   2.2  POST /api/abdm/v3/verifyaadhaar/otp     — Aadhaar OTP verify
 *   2.3  POST /api/abdm/v3/verifymobile/otp      — Mobile OTP verify
 *   2.4  GET  /api/abdm/getAbha/suggestions      — ABHA address suggestions
 *   2.5  POST /api/abdm/create/abha/address      — finalize ABHA address
 *   2.6  GET  /api/abdm/getAbha/profile/details  — pull profile via XToken
 *   2.7  GET  /api/abdm/getAbha/card             — pull card art via XToken
 *   4.1  POST /api/abdm/phr/web/login/abha/search    — search by PHR address
 *   4.2  POST /api/abdm/phr/web/login/abha/request/otp
 *   4.3  POST /api/abdm/phr/web/login/abha/verify
 */

const PAGE_SIZE = 25;

type VerifyMethod = 'aadhaar' | 'mobile' | 'abha-address' | 'abha-number';

/** Map raw ABDM JSON field names to the typed AbhaProfile interface. */
function normalizeAbdmProfile(raw: Record<string, unknown>): AbhaProfile {
  // yearOfBirth — ABDM may return as string "1990", float 1990.0, or 0.
  // Number() handles all forms; Math.floor strips decimals.
  const rawYob = raw.yearOfBirth ?? raw.birthYear;
  const parsedYob = rawYob !== null && rawYob !== undefined ? Math.floor(Number(rawYob)) : NaN;
  const yearOfBirth = !isNaN(parsedYob) && parsedYob >= 1900 && parsedYob <= 2200 ? parsedYob : undefined;

  // dayOfBirth / monthOfBirth — ABDM V3 returns these as STRING numbers ("15", "5"),
  // not JS numbers. Number() coerces both forms. Guard against 0 / NaN / empty.
  const parsedDay   = Number(raw.dayOfBirth);
  const parsedMonth = Number(raw.monthOfBirth);
  const dayOfBirth   = !isNaN(parsedDay)   && parsedDay   > 0 ? parsedDay   : undefined;
  const monthOfBirth = !isNaN(parsedMonth) && parsedMonth > 0 ? parsedMonth : undefined;

  // Build ISO date (YYYY-MM-DD).
  // Full DOB: use exact day/month/year.
  // Year-only: fall back to YYYY-01-01 so the backend always gets a parseable
  // LocalDate and can at least persist the yearOfBirth component.
  let dob: string | undefined;
  if (yearOfBirth) {
    if (dayOfBirth && monthOfBirth) {
      dob = `${yearOfBirth}-${String(monthOfBirth).padStart(2, '0')}-${String(dayOfBirth).padStart(2, '0')}`;
    } else {
      dob = `${yearOfBirth}-01-01`;  // year-only fallback
    }
  }

  // phrAddress may be an array (ABDM V3) — pick the first element.
  const phrAddr = raw.phrAddress;
  const abhaAddress = (
    raw.preferredAbhaAddress ??
    raw.abhaAddress ??
    (Array.isArray(phrAddr) && phrAddr.length > 0 ? phrAddr[0] : phrAddr)
  ) as string | undefined;

  return {
    abhaNumber:   (raw.ABHANumber  ?? raw.abhaNumber  ?? raw.healthIdNumber) as string | undefined,
    abhaAddress,
    name:         (raw.name ?? raw.fullName) as string | undefined,
    firstName:    raw.firstName   as string | undefined,
    middleName:   raw.middleName  as string | undefined,
    lastName:     raw.lastName    as string | undefined,
    gender:       raw.gender      as string | undefined,
    yearOfBirth,
    dayOfBirth,
    monthOfBirth,
    dob,
    mobile:       (raw.mobile ?? raw.communicationMobile) as string | undefined,
    email:        raw.email       as string | undefined,
    address:      raw.address     as string | undefined,
    pinCode:      (raw.pinCode ?? raw.pincode) as string | undefined,
    pincode:      (raw.pincode ?? raw.pinCode) as string | undefined,
    state:        (raw.stateName ?? raw.state) as string | undefined,
    district:     (raw.districtName ?? raw.district) as string | undefined,
  };
}

export function AbhaPage() {
  const { claims } = useAuth();
  const tenantId = claims?.tenantId ?? '';

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<PatientProfile | null>(null);

  const swrKey = ['patients', tenantId, search, page] as const;
  const { data, error, isLoading } = useSWR(
    tenantId ? swrKey : null,
    () => abha.listPatients({ tenantId, page, size: PAGE_SIZE, user: search || undefined }),
  );

  const rawPatients = data?.data ?? [];
  const patients: PatientProfile[] = rawPatients.map(p => ({
    ...p,
    abhaNumber: p.abhaNumber ?? p.abdmHealthId,
    abhaAddress: Array.isArray(p.abhaAddress) ? (p.abhaAddress as string[])[0] : p.abhaAddress,
    name: p.name ?? p.person?.name,
  }));
  const totalCount = data?.totalCount ?? patients.length;
  const pageEnvelope = {
    content: patients,
    totalElements: totalCount,
    totalPages: Math.max(Math.ceil(totalCount / PAGE_SIZE), 1),
    number: page,
    size: PAGE_SIZE,
    first: page === 0,
    last: (page + 1) * PAGE_SIZE >= totalCount,
    numberOfElements: patients.length,
  };

  const refresh = () => globalMutate((k) => Array.isArray(k) && k[0] === 'patients');
  const toast = useToast();

  const usePatient = (p: PatientProfile) => {
    const next: ActivePatient = {
      memberId: p.uhidNumber ?? p.memberNumber ?? p.id,
      abhaAddress: p.abhaAddress ?? '',
      abhaNumber: p.abhaNumber,
      name: p.name ?? p.person?.name ?? '',
      gender: (p.gender ?? 'unknown').toLowerCase(),
      birthDate: p.dob ?? (p.yearOfBirth ? `${p.yearOfBirth}-01-01` : undefined),
      mobile: p.mobile,
      address: typeof p.address === 'string' ? p.address : p.address?.line,
    };
    activePatient.set(next);
    toast.success('Active patient set', `${next.name || next.abhaAddress} — M2 & M3 forms will auto-fill.`);
  };

  return (
    <PageShell
      kicker="TENANT · ABHA"
      title="ABHA"
      subtitle="Onboard new patients via Aadhaar OTP or verify existing patients by ABHA address, mobile, or ABHA number. All calls are tenant-scoped."
      width="wide"
      actions={
        <Stack direction="row" spacing={1}>
          <IconButton onClick={refresh} aria-label="refresh"><RefreshIcon fontSize="small" /></IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setWizardOpen(true)}
            disabled={!tenantId}
          >
            New ABHA
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          placeholder="ABHA number, name, mobile…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          sx={{ minWidth: 320 }}
        />
      </Stack>

      <DataTable<PatientProfile>
        rows={patients}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        empty={{
          icon: AbhaIcon,
          title: 'No patients yet.',
          description: 'Onboard your first patient via Aadhaar OTP to populate the list.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}>New ABHA</Button>,
        }}
        onRowClick={(r) => setViewingProfile(r)}
        columns={[
          {
            header: 'Name',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.name ?? r.person?.name ?? '—'}</Typography>
                {r.uhidNumber && <Typography variant="caption" color="text.tertiary">UHID {r.uhidNumber}</Typography>}
              </Stack>
            ),
          },
          {
            header: 'ABHA',
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {r.abhaNumber ? redactMiddle(r.abhaNumber, 4, 4) : '—'}
                </Typography>
                {r.abhaAddress && <Typography variant="caption" color="text.tertiary">{r.abhaAddress}</Typography>}
              </Stack>
            ),
            width: 200,
          },
          { header: 'Mobile', cell: (r) => r.mobile ?? '—', width: 140 },
          { header: 'Gender', cell: (r) => r.gender ?? '—', width: 100 },
          {
            header: 'DOB',
            cell: (r) => {
              if (r.dob && r.dob.length >= 10 && !r.dob.endsWith('-01-01')) {
                // full date: show DD/MM/YYYY
                const [y, m, d] = r.dob.split('-');
                return <Typography variant="caption">{d}/{m}/{y}</Typography>;
              }
              if (r.yearOfBirth) return <Typography variant="caption" color="text.secondary">{r.yearOfBirth}</Typography>;
              return '—';
            },
            width: 110,
          },
          {
            header: 'Status',
            cell: (r) => (r.registered ? <StatusPill tone="success">Registered</StatusPill> : <StatusPill tone="mono">Pending</StatusPill>),
            width: 130,
          },
          {
            header: 'Updated',
            cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedDate)}</Typography>,
            width: 140,
          },
          {
            header: '',
            cell: (r) => (
              <Button
                size="small"
                variant="text"
                startIcon={<UsePatientIcon fontSize="small" />}
                onClick={(e) => { e.stopPropagation(); usePatient(r); }}
                disabled={!r.abhaAddress}
              >
                Use
              </Button>
            ),
            width: 90,
          },
        ]}
      />
      <Pagination page={pageEnvelope} onChange={setPage} />

      <UnifiedAbhaWizard
        open={wizardOpen}
        tenantId={tenantId}
        onClose={() => setWizardOpen(false)}
        onSettled={refresh}
      />
      <ProfileDialog patient={viewingProfile} tenantId={tenantId} onClose={() => setViewingProfile(null)} onRefresh={refresh} />
    </PageShell>
  );
}

// ===================================================================
// Unified ABHA wizard
// ===================================================================

function UnifiedAbhaWizard({
  open,
  tenantId,
  onClose,
  onSettled,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
  onSettled: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();

  const [method, setMethod] = useState<VerifyMethod | null>(null);
  // Aadhaar onboarding state
  const [txnId, setTxnId] = useState<string | null>(null);
  const [linkedMobile, setLinkedMobile] = useState<string | null>(null);
  const [communicationMobile, setCommunicationMobile] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<string[] | null>(null);
  const [suggestMessage, setSuggestMessage] = useState<string | null>(null);
  const [createdAddress, setCreatedAddress] = useState<string | null>(null);
  const [xToken, setXToken] = useState<string | null>(null);
  const [profile, setProfile] = useState<AbhaProfile | null>(null);
  // PHR-address verify state
  const [phrAddress, setPhrAddress] = useState<string | null>(null);
  const [phrAuthMethod, setPhrAuthMethod] = useState<'aadhaar' | 'mobile' | null>(null);
  // Mobile flow: account selection after OTP verify
  const [mobileAccounts, setMobileAccounts] = useState<{ ABHANumber?: string; name?: string; preferredAbhaAddress?: string }[]>([]);
  const [mobileTToken, setMobileTToken] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMethod(null);
      setTxnId(null);
      setLinkedMobile(null);
      setCommunicationMobile(null);
      setSuggestions(null);
      setSuggestMessage(null);
      setCreatedAddress(null);
      setXToken(null);
      setProfile(null);
      setPhrAddress(null);
      setPhrAuthMethod(null);
      setMobileAccounts([]);
      setMobileTToken(null);
    }
  }, [open]);

  // Build steps based on the chosen method. Step 0 is always the picker.
  const steps: WizardStep[] = useMemo(() => {
    const pick: WizardStep = {
      id: 'method',
      title: 'Verify by',
      hint: method ? labelFor(method) : 'choose',
      content: (ctrl) => <MethodPickerStep ctrl={ctrl} value={method} onChange={setMethod} />,
    };

    if (method === 'aadhaar') {
      return [
        pick,
        {
          id: 'aadhaar',
          title: 'Aadhaar',
          hint: communicationMobile ? `+ mobile ${communicationMobile.slice(-4).padStart(4, '•')}` : '12 digits',
          content: (ctrl) => (
            <EnrollAadhaarStep
              ctrl={ctrl}
              tenantId={tenantId}
              onTxn={(id, abdmLinkedMobile, enteredMobile) => {
                setTxnId(id);
                setLinkedMobile(abdmLinkedMobile ?? null);
                setCommunicationMobile(enteredMobile ?? null);
              }}
            />
          ),
        },
        {
          id: 'aadhaar-otp',
          title: 'Aadhaar OTP',
          hint: '6-digit OTP',
          content: (ctrl) => txnId ? (
            <EnrollAadhaarOtpStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId}
              linkedMobile={linkedMobile}
              communicationMobile={communicationMobile}
              onVerified={async (returnedProfile, returnedXToken) => {
                if (returnedXToken) setXToken(returnedXToken);
                if (returnedProfile?.abhaAddress) {
                  // Existing ABHA — skip create steps, jump to profile.
                  setProfile(returnedProfile);
                  setCreatedAddress(returnedProfile.abhaAddress);
                  return;
                }
                // No existing ABHA — preserve DOB/demographics from the enrollment
                // response (ABDM returns yearOfBirth/dayOfBirth/monthOfBirth here).
                // EnrollProfileStep will skip re-fetching since profile is already set.
                if (returnedProfile) setProfile(returnedProfile);
                // Pull suggestions to drive create step.
                try {
                  const r = await abha.suggestions({ tenantId, txnId });
                  setSuggestions(r.data?.abhaAddressList ?? r.data?.suggestions ?? []);
                  setSuggestMessage(r.data?.message ?? null);
                } catch (e) {
                  toast.error('Could not load suggestions', (e as Error).message);
                }
              }}
            />
          ) : <BackToOtp />,
        },
        {
          id: 'address',
          title: 'ABHA address',
          hint: profile?.abhaAddress && suggestions === null ? 'existing' : 'pick or create',
          content: (ctrl) => profile?.abhaAddress && suggestions === null ? (
            <ExistingAddressStep
              ctrl={ctrl}
              address={profile.abhaAddress}
              tenantId={tenantId}
              txnId={txnId ?? ''}
              onCreateNew={(sugg, msg) => {
                setSuggestions(sugg);
                setSuggestMessage(msg);
                // Clear existing address so we switch to EnrollAddressStep
                setProfile(prev => prev ? { ...prev, abhaAddress: undefined } : prev);
              }}
            />
          ) : (
            <EnrollAddressStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId ?? ''}
              suggestions={suggestions ?? []}
              suggestMessage={suggestMessage}
              onCreated={(addr, token) => { setCreatedAddress(addr); setXToken(token ?? null); }}
            />
          ),
        },
        {
          id: 'profile',
          title: 'Profile',
          hint: 'review',
          content: (ctrl) => (
            <EnrollProfileStep
              ctrl={ctrl}
              tenantId={tenantId}
              createdAddress={createdAddress}
              xToken={xToken}
              profile={profile}
              onProfile={setProfile}
              communicationMobile={communicationMobile}
            />
          ),
        },
      ];
    }

    if (method === 'abha-address') {
      return [
        pick,
        {
          id: 'phr-address',
          title: 'ABHA Address',
          hint: 'name@sbx',
          content: (ctrl) => (
            <PhrSearchStep
              ctrl={ctrl}
              tenantId={tenantId}
              onAddress={(addr, methods) => {
                setPhrAddress(addr);
                setPhrAuthMethod(methods.includes('AADHAAR_OTP') ? 'aadhaar' : 'mobile');
              }}
            />
          ),
        },
        {
          id: 'phr-otp-request',
          title: 'Send OTP',
          hint: phrAuthMethod === 'aadhaar' ? 'Aadhaar OTP' : 'Mobile OTP',
          content: (ctrl) => phrAddress && phrAuthMethod ? (
            <PhrRequestOtpStep ctrl={ctrl} tenantId={tenantId} phrAddress={phrAddress} authMethod={phrAuthMethod} onTxn={setTxnId} />
          ) : <BackToOtp />,
        },
        {
          id: 'phr-verify',
          title: 'Verify',
          hint: '6-digit OTP',
          content: (ctrl) => txnId && phrAuthMethod ? (
            <PhrVerifyStep ctrl={ctrl} tenantId={tenantId} txnId={txnId} authMethod={phrAuthMethod} onProfile={setProfile} onXToken={setXToken} />
          ) : <BackToOtp />,
        },
        {
          id: 'phr-profile',
          title: 'Profile',
          hint: 'review',
          content: (ctrl) => <PhrProfileStep ctrl={ctrl} tenantId={tenantId} profile={profile} xToken={xToken} />,
        },
      ];
    }

    if (method === 'mobile') {
      return [
        pick,
        {
          id: 'mobile',
          title: 'Mobile',
          hint: '10 digits',
          content: (ctrl) => (
            <MobileEntryStep
              ctrl={ctrl}
              tenantId={tenantId}
              onTxn={(id) => setTxnId(id)}
            />
          ),
        },
        {
          id: 'mobile-otp',
          title: 'Mobile OTP',
          hint: '6-digit OTP',
          content: (ctrl) => txnId ? (
            <MobileOtpStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId}
              onOtpDone={(accounts, tToken) => {
                setMobileAccounts(accounts);
                setMobileTToken(tToken);
              }}
            />
          ) : <BackToOtp />,
        },
        {
          id: 'mobile-select',
          title: 'Select ABHA',
          hint: mobileAccounts.length > 1 ? `${mobileAccounts.length} accounts` : 'confirm',
          content: (ctrl) => mobileTToken && txnId ? (
            <MobileAccountSelectStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId}
              accounts={mobileAccounts}
              tToken={mobileTToken}
              onProfile={setProfile}
              onXToken={setXToken}
            />
          ) : <BackToOtp />,
        },
        {
          id: 'mobile-profile',
          title: 'Profile',
          hint: 'review',
          content: (ctrl) => <PhrProfileStep ctrl={ctrl} tenantId={tenantId} profile={profile} xToken={xToken} />,
        },
      ];
    }

    if (method === 'abha-number') {
      return [
        pick,
        {
          id: 'abha-number',
          title: 'ABHA Number',
          hint: '14 digits',
          content: (ctrl) => (
            <AbhaNumberSearchStep
              ctrl={ctrl}
              tenantId={tenantId}
              onTxn={(id) => setTxnId(id)}
            />
          ),
        },
        {
          id: 'abha-number-otp',
          title: 'Mobile OTP',
          hint: '6-digit OTP',
          content: (ctrl) => txnId ? (
            <MobileOtpStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId}
              onOtpDone={(accounts, tToken) => {
                setMobileAccounts(accounts);
                setMobileTToken(tToken);
              }}
            />
          ) : <BackToOtp />,
        },
        {
          id: 'abha-number-select',
          title: 'Select ABHA',
          hint: mobileAccounts.length > 1 ? `${mobileAccounts.length} accounts` : 'confirm',
          content: (ctrl) => mobileTToken && txnId ? (
            <MobileAccountSelectStep
              ctrl={ctrl}
              tenantId={tenantId}
              txnId={txnId}
              accounts={mobileAccounts}
              tToken={mobileTToken}
              onProfile={setProfile}
              onXToken={setXToken}
            />
          ) : <BackToOtp />,
        },
        {
          id: 'abha-number-profile',
          title: 'Profile',
          hint: 'review',
          content: (ctrl) => <PhrProfileStep ctrl={ctrl} tenantId={tenantId} profile={profile} xToken={xToken} />,
        },
      ];
    }

    // Method not picked yet — only show the picker so the user can't advance.
    return [pick];
  }, [
    method, tenantId, txnId, linkedMobile, communicationMobile, suggestions, suggestMessage,
    createdAddress, xToken, profile, phrAddress, phrAuthMethod, mobileAccounts, mobileTToken, toast,
  ]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600, letterSpacing: -0.2 }}>
        New ABHA
        <Typography variant="caption" color="text.tertiary" sx={{ display: 'block' }}>
          Verify or onboard a patient — pick a verification method to continue.
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Wizard
          steps={steps}
          onComplete={() => {
            const summary = profile?.name ?? createdAddress ?? phrAddress ?? undefined;
            toast.success(method === 'aadhaar' && !profile?.abhaAddress ? 'ABHA Address created' : 'Patient verified', summary);
            // Promote the just-onboarded patient into the session-level
            // active patient store so M2 / M3 forms auto-fill from it.
            // memberId falls back to abhaAddress until the backend round-trip
            // assigns a UHID — the Patient Picker can re-pick once it lands.
            const finalAbha = profile?.abhaAddress ?? createdAddress ?? phrAddress;
            const finalName = [profile?.firstName, profile?.middleName, profile?.lastName].filter(Boolean).join(' ').trim()
              || profile?.name
              || finalAbha
              || '';
            if (finalAbha) {
              const next: ActivePatient = {
                memberId: finalAbha,
                abhaAddress: finalAbha,
                abhaNumber: profile?.abhaNumber,
                name: finalName,
                gender: (profile?.gender ?? 'unknown').toLowerCase(),
                birthDate: profile?.yearOfBirth ? `${profile.yearOfBirth}-01-01` : undefined,
                mobile: profile?.mobile ?? communicationMobile ?? linkedMobile ?? undefined,
                address: profile?.address,
              };
              activePatient.set(next);
            }
            onSettled();
            onClose();
          }}
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

function labelFor(m: VerifyMethod): string {
  switch (m) {
    case 'aadhaar': return 'Aadhaar number';
    case 'mobile': return 'Mobile number';
    case 'abha-address': return 'ABHA Address';
    case 'abha-number': return 'ABHA Number';
  }
}

function BackToOtp() {
  return <Typography variant="body2" color="text.secondary">Missing transaction id — go back to the previous step.</Typography>;
}

// ===================================================================
// Step 0 — method picker
// ===================================================================

function MethodPickerStep({
  ctrl,
  value,
  onChange,
}: {
  ctrl: WizardStepController;
  value: VerifyMethod | null;
  onChange: (m: VerifyMethod) => void;
}) {
  const theme = useTheme();

  useEffect(() => {
    ctrl.setNextLabel('Continue');
    ctrl.setNextEnabled(!!value);
    ctrl.setNextHandler(null);
  }, [value, ctrl]);

  const options: { id: VerifyMethod; title: string; hint: string; icon: typeof AadhaarIcon }[] = [
    { id: 'aadhaar',      title: 'Aadhaar',       hint: 'Onboard a new patient via Aadhaar OTP.',                  icon: AadhaarIcon },
    { id: 'abha-address', title: 'ABHA Address',  hint: 'Verify an existing patient by their PHR address.',        icon: AbhaAddressIcon },
    { id: 'mobile',       title: 'Mobile Number', hint: 'Verify an existing patient by their registered mobile.', icon: MobileIcon },
    { id: 'abha-number',  title: 'ABHA Number',   hint: '14-digit ABHA / health-id lookup.',                       icon: AbhaNumberIcon },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>How do you want to verify the patient?</Typography>
        <Typography variant="body2" color="text.secondary">
          Pick a method. Aadhaar onboards new patients; the others look up patients who already have an ABHA.
        </Typography>
      </Stack>
      <RadioGroup value={value ?? ''} onChange={(_, v) => onChange(v as VerifyMethod)}>
        <Stack spacing={1.25}>
          {options.map((opt) => {
            const Icon = opt.icon;
            const selected = value === opt.id;
            return (
              <Box
                key={opt.id}
                onClick={() => onChange(opt.id)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  borderRadius: theme.radius.sm + 'px',
                  border: `1.5px solid ${selected ? theme.palette.brand.main : theme.palette.border.subtle}`,
                  bgcolor: selected ? theme.palette.surface.sunken : 'transparent',
                  cursor: 'pointer',
                  transition: 'border-color 120ms, background-color 120ms',
                }}
              >
                <Radio size="small" value={opt.id} checked={selected} sx={{ p: 0, mt: 0.25 }} />
                <Box sx={{ width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
                  <Icon fontSize="small" />
                </Box>
                <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{opt.title}</Typography>
                  <Typography variant="caption" color="text.tertiary">{opt.hint}</Typography>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </RadioGroup>
    </Stack>
  );
}

// ===================================================================
// Aadhaar onboarding steps (M1 doc §2.1 – §2.7)
// ===================================================================

const aadhaarSchema = z.object({
  aadhaar: z.string().regex(/^\d{12}$/, 'Aadhaar must be exactly 12 digits'),
  mobile: z.string()
    .regex(/^\d{10}$/, 'Mobile must be exactly 10 digits'),
});
type AadhaarForm = z.infer<typeof aadhaarSchema>;

function EnrollAadhaarStep({
  ctrl,
  tenantId,
  onTxn,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  /**
   * @param abdmLinkedMobile masked mobile ABDM returns for the Aadhaar
   * @param enteredMobile    optional communication mobile the operator typed
   */
  onTxn: (txnId: string, abdmLinkedMobile?: string, enteredMobile?: string) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<AadhaarForm>({
    defaultValues: { aadhaar: '', mobile: '' },
  });
  const aadhaar = watch('aadhaar');
  const mobile = watch('mobile');

  useEffect(() => {
    ctrl.setNextLabel('Send OTP');
    ctrl.setNextEnabled(/^\d{12}$/.test(aadhaar) && /^\d{10}$/.test(mobile));
  }, [aadhaar, mobile, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = aadhaarSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.requestOtp({ tenantId, type: 'aadhaar', value: parsed.data.aadhaar, txnId: '' });
        const id = r.data?.txnId;
        if (!id) {
          toast.error('OTP request failed', r.message ?? r.data?.message ?? 'No transaction id returned.');
          return false;
        }
        onTxn(id, r.data?.mobileNumber, parsed.data.mobile?.trim() || undefined);
        toast.success('OTP sent', r.data?.mobileNumber ? `to ${r.data.mobileNumber}` : undefined);
        return true;
      } catch (e) {
        toast.error('OTP request failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, onTxn, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Patient's Aadhaar</Typography>
        <Typography variant="body2" color="text.secondary">
          ABDM sends a one-time password to the mobile linked to this Aadhaar.
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="Aadhaar number"
        placeholder="12 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 12 }}
        {...register('aadhaar')}
        error={!!errors.aadhaar}
        helperText={errors.aadhaar?.message ?? ' '}
      />
      <TextField
        label="Patient mobile number"
        placeholder="10 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
        {...register('mobile')}
        error={!!errors.mobile}
        helperText={errors.mobile?.message ?? 'Required by ABDM for enrollment (10 digits, no +91)'}
      />
    </Stack>
  );
}

const aadhaarOtpSchema = z.object({
  otpValue: z.string().regex(/^\d{6}$/, 'Enter the 6-digit OTP'),
});
type AadhaarOtpForm = z.infer<typeof aadhaarOtpSchema>;

function EnrollAadhaarOtpStep({
  ctrl,
  tenantId,
  txnId,
  linkedMobile,
  communicationMobile,
  onVerified,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  txnId: string;
  /** Masked mobile ABDM returned for this Aadhaar — shown only as a hint. */
  linkedMobile: string | null;
  /** Optional communication mobile the operator typed in step 1. */
  communicationMobile: string | null;
  /** Called with normalized profile + xToken (both may be undefined for brand-new accounts). */
  onVerified: (profile?: AbhaProfile, xToken?: string | null) => Promise<void>;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<AadhaarOtpForm>({
    defaultValues: { otpValue: '' },
  });
  const otp = watch('otpValue');

  useEffect(() => {
    ctrl.setNextLabel('Verify');
    ctrl.setNextEnabled(/^\d{6}$/.test(otp));
  }, [otp, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = aadhaarOtpSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.verifyAadhaarOtp({
          tenantId, txnId,
          otpValue: parsed.data.otpValue,
          ...(communicationMobile ? { mobile: communicationMobile } : {}),
        });
        if (r.status === false) {
          const msg = (r as { errorMessage?: string; message?: string }).errorMessage
            ?? (r as { message?: string }).message
            ?? 'OTP verification failed';
          toast.error('Aadhaar OTP failed', msg);
          return false;
        }
        // Normalize the raw ABDM ABHAProfile (field names differ from our types).
        const rawData = r.data as Record<string, unknown> | undefined;
        const rawABHA = rawData?.ABHAProfile as Record<string, unknown> | undefined;
        const normalizedProfile = rawABHA ? normalizeAbdmProfile(rawABHA) : undefined;
        // Capture XToken for ABHA card fetch (present even when isNew===false).
        const tokens = rawData?.tokens as Record<string, unknown> | undefined;
        const extractedXToken = (tokens?.token ?? rawData?.token ?? rawData?.XToken) as string | null ?? null;
        await onVerified(normalizedProfile, extractedXToken || null);
        toast.success('Aadhaar OTP verified');
        return true;
      } catch (e) {
        toast.error('OTP verification failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, txnId, communicationMobile, onVerified, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Enter the OTP</Typography>
        <Typography variant="body2" color="text.secondary">
          {linkedMobile ? `Six-digit code sent to ${linkedMobile}.` : 'Six-digit code sent to the registered mobile.'}
          {communicationMobile && ` Future notifications will go to ${communicationMobile}.`}
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="OTP"
        placeholder="6 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { fontFamily: 'JetBrains Mono, monospace', letterSpacing: 4, fontSize: 20 } }}
        {...register('otpValue')}
        error={!!errors.otpValue}
        helperText={errors.otpValue?.message ?? `Transaction id ${redactMiddle(txnId, 4, 4)}`}
      />
    </Stack>
  );
}

function ExistingAddressStep({
  ctrl,
  address,
  tenantId,
  txnId,
  onCreateNew,
}: {
  ctrl: WizardStepController;
  address: string;
  tenantId: string;
  txnId: string;
  onCreateNew: (suggestions: string[], message: string | null) => void;
}) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Wizard footer button = "Continue with Existing"
    ctrl.setNextLabel('Continue with Existing');
    ctrl.setNextEnabled(true);
    ctrl.setNextHandler(null);
  }, [ctrl]);

  return (
    <Stack spacing={3}>
      <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Existing ABHA detected</Typography>

      <Alert severity="info" variant="outlined">
        This Aadhaar already has an ABHA Address linked: <strong>{address}</strong>.
      </Alert>

      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
        You can continue with the existing ABHA address or create a new one.
      </Typography>

      {/* Create new ABHA address */}
      <Button
        fullWidth
        variant="outlined"
        disabled={loading}
        onClick={async () => {
          setLoading(true);
          try {
            const r = await abha.suggestions({ tenantId, txnId });
            const sugg = r.data?.abhaAddressList ?? r.data?.suggestions ?? [];
            const msg = r.data?.message ?? null;
            onCreateNew(sugg, msg);
          } catch (e) {
            toast.error('Could not load suggestions', (e as Error).message);
          } finally {
            setLoading(false);
          }
        }}
        sx={{
          py: 1.3, fontWeight: 700, borderRadius: 2, textTransform: 'none',
          border: '2px solid #3b82f6', bgcolor: '#eff6ff', color: '#1d4ed8',
          '&:hover': { bgcolor: '#dbeafe', borderColor: '#2563eb' },
        }}
      >
        {loading ? 'Loading suggestions…' : 'Create New ABHA Address'}
      </Button>
    </Stack>
  );
}

function EnrollAddressStep({
  ctrl,
  tenantId,
  txnId,
  suggestions,
  suggestMessage,
  onCreated,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  txnId: string;
  suggestions: string[];
  suggestMessage: string | null;
  onCreated: (addr: string, xToken: string | null) => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [mode, setMode] = useState<'suggested' | 'custom'>(() => suggestions.length > 0 ? 'suggested' : 'custom');
  const [selected, setSelected] = useState('');
  const [custom, setCustom] = useState('');
  const [preferred, setPreferred] = useState(true);

  const accountAlreadyExists = useMemo(() => {
    const m = (suggestMessage ?? '').toLowerCase();
    return suggestions.length === 0 || m.includes('exist') || m.includes('already');
  }, [suggestions.length, suggestMessage]);

  useEffect(() => {
    if (suggestions.length && !selected) setSelected(suggestions[0] ?? '');
    if (suggestions.length === 0) setMode('custom');
  }, [suggestions, selected]);

  // Strip @sbx (suggestion list has the suffix; create endpoint expects bare).
  const value = mode === 'suggested'
    ? selected.replace(/@\w+$/, '')
    : custom.trim().replace(/@\w+$/, '');

  useEffect(() => {
    ctrl.setNextLabel('Create');
    ctrl.setNextEnabled(value.length >= 3);
  }, [value, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      try {
        const r = await abha.createAbhaAddress({
          tenantId, txnId,
          abhaAddress: value,
          preferred: preferred ? '1' : '0',
        });
        const data = r.data;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const xToken = (r as any)?.XToken ?? (data as any)?.XToken ?? (data as any)?.xToken ?? (data as any)?.tokens?.token ?? null;
        const finalAddr = data?.abhaAddress ?? `${value}@sbx`;
        onCreated(finalAddr, xToken);
        toast.success('ABHA Address created', r.message ?? finalAddr);
        return true;
      } catch (e) {
        const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
        const msg = detail?.message ?? detail?.error ?? (e as Error).message;
        if (/already exists/i.test(msg)) {
          toast.error('ABHA Address taken', 'Pick a different one — that address is already in use.');
        } else if (/invalid/i.test(msg)) {
          toast.error('Invalid ABHA Address', msg);
        } else {
          toast.error('Could not create ABHA Address', msg);
        }
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [tenantId, txnId, value, preferred, onCreated, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Pick or create an ABHA Address</Typography>
        <Typography variant="body2" color="text.secondary">
          Suggestions are reserved for this enrollment. Custom addresses must be unique across ABDM.
        </Typography>
      </Stack>

      {accountAlreadyExists && (
        <Alert severity="warning" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
          {suggestMessage ?? 'No fresh suggestions returned for this Aadhaar.'} The patient may already have an ABHA
          Address. Try a different name below, or close this dialog and verify the existing patient under
          <strong> Verify by → ABHA Address</strong>.
        </Alert>
      )}

      <RadioGroup row value={mode} onChange={(_, v) => setMode(v as 'suggested' | 'custom')}>
        <FormControlLabel
          value="suggested"
          control={<Radio size="small" />}
          label={`Suggested${suggestions.length ? ` (${suggestions.length})` : ''}`}
          disabled={suggestions.length === 0}
        />
        <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom" />
      </RadioGroup>

      {mode === 'suggested' ? (
        <Stack spacing={1}>
          {suggestions.length === 0 ? (
            <Typography variant="caption" color="text.tertiary">
              No suggestions — switch to Custom and type an address.
            </Typography>
          ) : (
            <RadioGroup value={selected} onChange={(_, v) => setSelected(v)}>
              {suggestions.map((s) => (
                <Box
                  key={s}
                  onClick={() => setSelected(s)}
                  sx={{
                    p: 1.25,
                    borderRadius: theme.radius.sm + 'px',
                    border: `1.5px solid ${selected === s ? theme.palette.brand.main : theme.palette.border.subtle}`,
                    bgcolor: selected === s ? theme.palette.surface.sunken : 'transparent',
                    cursor: 'pointer',
                    mb: 0.5,
                  }}
                >
                  <FormControlLabel
                    value={s}
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        {s}
                      </Typography>
                    }
                  />
                </Box>
              ))}
            </RadioGroup>
          )}
        </Stack>
      ) : (
        <TextField
          autoFocus
          label="ABHA Address"
          placeholder="yourname"
          fullWidth
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          InputProps={{ endAdornment: <InputAdornment position="end">@sbx</InputAdornment> }}
          helperText="Letters, numbers, dot, underscore, hyphen. ABDM will reject if already taken."
        />
      )}

      <FormControlLabel
        control={<Radio size="small" checked={preferred} onClick={() => setPreferred(!preferred)} />}
        label={
          <Typography variant="caption" color="text.secondary">
            Mark as preferred ABHA Address ({preferred ? 'preferred=1' : 'preferred=0'})
          </Typography>
        }
      />
    </Stack>
  );
}

function EnrollProfileStep({
  ctrl,
  tenantId,
  createdAddress,
  xToken,
  profile,
  onProfile,
  communicationMobile,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  createdAddress: string | null;
  xToken: string | null;
  profile: AbhaProfile | null;
  onProfile: (p: AbhaProfile) => void;
  communicationMobile: string | null;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [cardBase64, setCardBase64] = useState<string | null>(null);
  const [revisitOpen, setRevisitOpen] = useState(false);
  const [revisitData, setRevisitData] = useState<CheckRevisitResponse | null>(null);
  const revisitResolveRef = useRef<((choice: 'hmis' | 'abdm' | 'cancel') => void) | null>(null);

  useEffect(() => {
    ctrl.setNextLabel('Register Patient');
    ctrl.setNextEnabled(!!profile);
    ctrl.setNextHandler(!profile ? null : async () => {
      const fullName = [profile.firstName, profile.middleName, profile.lastName]
        .filter(Boolean).join(' ').trim() || profile.name || '';
      const saveBody = {
        tenantId,
        abhaAddress: profile.abhaAddress ?? createdAddress ?? undefined,
        abdmHealthId: profile.abhaNumber,
        mobile: profile.mobile ?? communicationMobile ?? undefined,
        yearOfBirth: profile.yearOfBirth,
        dob: profile.dob,
        gender: profile.gender,
        person: { name: fullName },
        address: typeof profile.address === 'string' ? profile.address : undefined,
        pincode: profile.pincode ?? profile.pinCode,
        district: profile.district,
        state: profile.state,
      };
      try {
        // Check if patient already exists before saving.
        const revisitResp = await abha.checkRevisit(saveBody).catch(() => null);
        if (revisitResp?.isExistingPatient) {
          setRevisitData(revisitResp);
          setRevisitOpen(true);
          const choice = await new Promise<'hmis' | 'abdm' | 'cancel'>((resolve) => {
            revisitResolveRef.current = resolve;
          });
          if (choice === 'cancel') return false;
          if (choice === 'hmis') {
            toast.success('Using existing patient record');
            return true;
          }
          // 'abdm' — fall through and save with ABDM data
        }
        await abha.savePatient(saveBody);
        toast.success('Patient registered', fullName || profile.abhaAddress);
        return true;
      } catch (e) {
        toast.error('Registration failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [ctrl, profile, tenantId, communicationMobile, createdAddress, toast]);

  // Auto-fetch profile via XToken (only set on the create-new branch).
  useEffect(() => {
    if (!xToken || profile) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await abha.fetchProfileByXToken({ tenantId, XToken: xToken });
        if (cancelled) return;
        if (r.data) onProfile(normalizeAbdmProfile(r.data as Record<string, unknown>));
      } catch (e) {
        if (!cancelled) toast.error('Could not fetch profile', (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, xToken, profile, onProfile, toast]);

  const fetchCard = async () => {
    if (!xToken) return;
    try {
      const r = await abha.fetchCardByXToken({ tenantId, XToken: xToken });
      const cardData = (r as any)?.data as string | undefined;
      if (cardData) {
        setCardBase64(cardData);
        toast.success('ABHA card fetched');
      } else {
        toast.error('No card returned — ' + ((r as any)?.message ?? 'unknown error'));
      }
    } catch (e) {
      toast.error('Could not fetch card', (e as Error).message);
    }
  };

  const downloadCard = () => {
    if (!cardBase64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${cardBase64}`;
    link.download = `ABHA_Card_${profile?.name ?? 'card'}.png`;
    link.click();
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>
          {profile?.name ?? 'ABHA profile'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {createdAddress ? `ABHA Address: ${createdAddress}` : 'No address available.'}
        </Typography>
      </Stack>
      {loading && <Typography variant="body2" color="text.secondary">Fetching profile…</Typography>}
      {profile && (
        <Box>
          <FieldRow label="Name" value={
            [profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ').trim()
            || profile.name || '—'
          } />
          <FieldRow label="ABHA address" value={profile.abhaAddress ?? createdAddress ?? '—'} mono />
          <FieldRow label="ABHA number"  value={profile.abhaNumber ?? '—'} mono />
          {profile.gender && <FieldRow label="Gender" value={profile.gender} />}
          {profile.yearOfBirth && <FieldRow label="Year of birth" value={profile.yearOfBirth} />}
          {(profile.mobile || communicationMobile) && (
            <FieldRow label="Mobile" value={profile.mobile ?? communicationMobile ?? '—'} />
          )}
          {profile.address && (
            <FieldRow label="Address" value={typeof profile.address === 'string' ? profile.address : '—'} />
          )}
          {profile.state && <FieldRow label="State" value={profile.state} />}
          {profile.district && <FieldRow label="District" value={profile.district} />}
          {(profile.pincode || profile.pinCode) && (
            <FieldRow label="Pincode" value={profile.pincode ?? profile.pinCode ?? '—'} />
          )}
        </Box>
      )}
      {cardBase64 ? (
        <Box>
          <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>ABHA card</Typography>
          <Box
            component="img"
            src={`data:image/png;base64,${cardBase64}`}
            alt="ABHA card"
            sx={{
              maxWidth: '100%',
              borderRadius: theme.radius.sm + 'px',
              border: `1px solid ${theme.palette.border.subtle}`,
            }}
          />
          <Button variant="contained" size="small" onClick={downloadCard} sx={{ mt: 1 }}>
            Download ABHA Card
          </Button>
        </Box>
      ) : xToken && (
        <Button variant="outlined" onClick={fetchCard} sx={{ alignSelf: 'flex-start' }}>
          View &amp; Download ABHA Card
        </Button>
      )}
      <Alert severity="info" variant="outlined" sx={{ borderRadius: theme.radius.sm + 'px' }}>
        Review the profile above, then click <strong>Register Patient</strong> to save them to the patient list.
      </Alert>
      <PatientRevisitDialog
        open={revisitOpen}
        revisitData={revisitData}
        newProfile={profile}
        onChoose={(choice) => { setRevisitOpen(false); revisitResolveRef.current?.(choice); }}
        onClose={() => { setRevisitOpen(false); revisitResolveRef.current?.('cancel'); }}
      />
    </Stack>
  );
}

// ===================================================================
// Verify-existing steps (M1 doc §4.x — by ABHA Address / Mobile / ABHA Number)
// ===================================================================

const phrSearchSchema = z.object({
  abhaAddress: z.string().regex(/^[A-Za-z0-9._-]+@[A-Za-z]+$/, 'Enter a PHR address like name@sbx'),
});
type PhrSearchForm = z.infer<typeof phrSearchSchema>;

function PhrSearchStep({
  ctrl,
  tenantId,
  onAddress,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  onAddress: (addr: string, authMethods: ('AADHAAR_OTP' | 'MOBILE_OTP')[]) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<PhrSearchForm>({
    defaultValues: { abhaAddress: '' },
  });
  const value = watch('abhaAddress');

  useEffect(() => {
    ctrl.setNextLabel('Search');
    ctrl.setNextEnabled(/^[A-Za-z0-9._-]+@[A-Za-z]+$/.test(value));
  }, [value, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = phrSearchSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.searchByAddress({ tenantId, abhaAddress: parsed.data.abhaAddress });
        const data = r.data;
        if (!data?.authMethods?.length) {
          toast.error('No auth methods available', 'ABDM did not return any auth methods for this PHR.');
          return false;
        }
        onAddress(parsed.data.abhaAddress, data.authMethods);
        toast.success('Patient found', data.name ?? data.abhaAddress);
        return true;
      } catch (e) {
        toast.error('Search failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, onAddress, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Patient&apos;s ABHA Address</Typography>
        <Typography variant="body2" color="text.secondary">
          ABDM returns the available auth methods (Aadhaar OTP / Mobile OTP).
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="ABHA Address"
        placeholder="arjun_2002@sbx"
        fullWidth
        {...register('abhaAddress')}
        error={!!errors.abhaAddress}
        helperText={errors.abhaAddress?.message ?? ' '}
      />
    </Stack>
  );
}

const abhaNumberSchema = z.object({
  abhaNumber: z.string().regex(/^\d{2}-?\d{4}-?\d{4}-?\d{4}$/, 'Enter the 14-digit ABHA number'),
});
type AbhaNumberForm = z.infer<typeof abhaNumberSchema>;

function AbhaNumberSearchStep({
  ctrl,
  tenantId,
  onTxn,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  onTxn: (txnId: string) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<AbhaNumberForm>({
    defaultValues: { abhaNumber: '' },
  });
  const value = watch('abhaNumber');

  useEffect(() => {
    ctrl.setNextLabel('Send OTP');
    ctrl.setNextEnabled(/^\d{2}-?\d{4}-?\d{4}-?\d{4}$/.test(value));
  }, [value, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = abhaNumberSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        // ABDM ABHA number login: type='mobile' → backend maps loginHint='abha-number',
        // scope=['abha-login','mobile-verify'], otpSystem='abdm'
        // Sends OTP to the mobile linked to this ABHA number.
        const bare = parsed.data.abhaNumber.replace(/-/g, '');
        const r = await abha.profileLoginRequestOtp({ tenantId, type: 'mobile', value: bare, txnId: '' });
        const txnId = r.data?.txnId;
        if (!txnId) {
          toast.error('OTP request failed', r.message ?? 'No transaction ID returned.');
          return false;
        }
        onTxn(txnId);
        toast.success('OTP sent', 'Check the mobile linked to this ABHA number.');
        return true;
      } catch (e) {
        toast.error('OTP request failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, onTxn, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Patient&apos;s ABHA Number</Typography>
        <Typography variant="body2" color="text.secondary">
          Enter the 14-digit ABHA number. An OTP will be sent to the mobile linked to this ABHA.
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="ABHA Number"
        placeholder="91-2345-6789-1234"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 17 }}
        {...register('abhaNumber')}
        error={!!errors.abhaNumber}
        helperText={errors.abhaNumber?.message ?? ' '}
      />
    </Stack>
  );
}

const mobileSchema = z.object({ mobile: z.string().regex(/^\d{10}$/, 'Enter a 10-digit mobile number') });
type MobileForm = z.infer<typeof mobileSchema>;

function MobileEntryStep({
  ctrl,
  tenantId,
  onTxn,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  onTxn: (txnId: string) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<MobileForm>({ defaultValues: { mobile: '' } });
  const mobile = watch('mobile');

  useEffect(() => {
    ctrl.setNextLabel('Send OTP');
    ctrl.setNextEnabled(/^\d{10}$/.test(mobile));
  }, [mobile, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = mobileSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.profileLoginRequestOtp({ tenantId, type: 'communication-mobile', value: parsed.data.mobile, txnId: '' });
        const id = r.data?.txnId;
        if (!id) {
          toast.error('OTP request failed', r.message ?? 'No transaction id returned.');
          return false;
        }
        onTxn(id);
        toast.success('OTP sent', `to ${parsed.data.mobile}`);
        return true;
      } catch (e) {
        toast.error('OTP request failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, onTxn, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Patient&apos;s mobile number</Typography>
        <Typography variant="body2" color="text.secondary">
          The patient must already have an ABHA linked to this mobile.
        </Typography>
      </Stack>
      <TextField
        autoFocus
        label="Mobile number"
        placeholder="10 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 10 }}
        {...register('mobile')}
        error={!!errors.mobile}
        helperText={errors.mobile?.message ?? ' '}
      />
    </Stack>
  );
}

const otpSchema = z.object({ otp: z.string().regex(/^\d{6}$/, 'Enter the 6-digit OTP') });
type OtpForm = z.infer<typeof otpSchema>;

function MobileOtpStep({
  ctrl,
  tenantId,
  txnId,
  onOtpDone,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  txnId: string;
  /** Receives the accounts list + T-Token after OTP is verified. */
  onOtpDone: (accounts: { ABHANumber?: string; name?: string; preferredAbhaAddress?: string }[], tToken: string) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<OtpForm>({ defaultValues: { otp: '' } });
  const otp = watch('otp');

  useEffect(() => {
    ctrl.setNextLabel('Verify');
    ctrl.setNextEnabled(/^\d{6}$/.test(otp));
  }, [otp, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = otpSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.profileLoginVerify({ tenantId, txnId, otp: parsed.data.otp, type: 'communication-mobile' });
        // ABDM returns T-Token (typ:Transfer) in `token` or `t_token` + accounts list.
        const tToken = r.data?.token ?? r.data?.t_token;
        if (!tToken) {
          toast.error('OTP verification failed', r.message ?? 'No token returned');
          return false;
        }
        onOtpDone(r.data?.accounts ?? [], tToken);
        toast.success('OTP verified — select your ABHA below');
        return true;
      } catch (e) {
        toast.error('OTP verification failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, txnId, onOtpDone, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Enter the OTP</Typography>
        <Typography variant="body2" color="text.secondary">Six-digit code sent to the registered mobile.</Typography>
      </Stack>
      <TextField
        autoFocus
        label="OTP"
        placeholder="6 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { fontFamily: 'JetBrains Mono, monospace', letterSpacing: 4, fontSize: 20 } }}
        {...register('otp')}
        error={!!errors.otp}
        helperText={errors.otp?.message ?? `Transaction id ${redactMiddle(txnId, 4, 4)}`}
      />
    </Stack>
  );
}

function MobileAccountSelectStep({
  ctrl,
  tenantId,
  txnId,
  accounts,
  tToken,
  onProfile,
  onXToken,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  txnId: string;
  accounts: { ABHANumber?: string; name?: string; preferredAbhaAddress?: string }[];
  tToken: string;
  onProfile: (p: AbhaProfile) => void;
  onXToken?: (t: string) => void;
}) {
  const toast = useToast();
  const [selected, setSelected] = useState(() => accounts[0]?.ABHANumber ?? '');

  useEffect(() => {
    ctrl.setNextLabel('Confirm');
    ctrl.setNextEnabled(!!selected);
  }, [selected, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      if (!selected) return false;
      try {
        const verifyRes = await abha.profileLoginVerifyUser({ tenantId, ABHANumber: selected, txnId, tToken });
        const sessionToken = verifyRes.data?.token;
        if (!sessionToken) {
          toast.error('Account selection failed', verifyRes.message ?? 'No session token returned');
          return false;
        }
        onXToken?.(sessionToken);
        const profileRes = await abha.profileAccount({ tenantId, XToken: sessionToken });
        if (!profileRes.data) {
          toast.error('Profile fetch failed', profileRes.message ?? 'No profile returned');
          return false;
        }
        const normalizedMobile = normalizeAbdmProfile(profileRes.data as Record<string, unknown>);
        onProfile(normalizedMobile);
        toast.success('ABHA verified', normalizedMobile.name ?? normalizedMobile.abhaAddress ?? undefined);
        return true;
      } catch (e) {
        toast.error('Account selection failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [selected, tenantId, txnId, tToken, onProfile, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Select ABHA Account</Typography>
        <Typography variant="body2" color="text.secondary">
          {accounts.length === 1
            ? 'One ABHA is linked to this mobile. Confirm to continue.'
            : `${accounts.length} ABHA accounts found. Select the one to verify.`}
        </Typography>
      </Stack>
      <RadioGroup value={selected} onChange={(e) => setSelected(e.target.value)}>
        {accounts.map((acc) => (
          <FormControlLabel
            key={acc.ABHANumber}
            value={acc.ABHANumber ?? ''}
            control={<Radio size="small" />}
            label={
              <Stack>
                <Typography variant="body2" fontWeight={600}>{acc.name ?? 'Unknown'}</Typography>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  {acc.ABHANumber ?? ''}{acc.preferredAbhaAddress ? ` · ${acc.preferredAbhaAddress}` : ''}
                </Typography>
              </Stack>
            }
          />
        ))}
      </RadioGroup>
      {accounts.length === 0 && (
        <Alert severity="warning">No ABHA accounts returned. Please go back and retry.</Alert>
      )}
    </Stack>
  );
}

function PhrRequestOtpStep({
  ctrl,
  tenantId,
  phrAddress,
  authMethod,
  onTxn,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  phrAddress: string;
  authMethod: 'aadhaar' | 'mobile';
  onTxn: (txnId: string) => void;
}) {
  const toast = useToast();

  useEffect(() => {
    ctrl.setNextLabel('Send OTP');
    ctrl.setNextEnabled(true);
  }, [ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      try {
        const r = await abha.phrRequestOtp({ tenantId, type: authMethod, value: phrAddress, txnId: '' });
        const id = r.data?.txnId;
        if (!id) {
          toast.error('OTP request failed', r.data?.message ?? 'No transaction id returned.');
          return false;
        }
        onTxn(id);
        toast.success('OTP sent', r.data?.mobileNumber ? `to ${r.data.mobileNumber}` : undefined);
        return true;
      } catch (e) {
        toast.error('OTP request failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [tenantId, phrAddress, authMethod, onTxn, toast, ctrl]);

  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        ABDM will send an OTP via <strong>{authMethod === 'aadhaar' ? 'Aadhaar-linked mobile' : 'registered mobile'}</strong>
        {' '}for <code>{phrAddress}</code>.
      </Typography>
      <Typography variant="caption" color="text.tertiary">
        Click <strong>Send OTP</strong> to continue.
      </Typography>
    </Stack>
  );
}

function PhrVerifyStep({
  ctrl,
  tenantId,
  txnId,
  authMethod,
  onProfile,
  onXToken,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  txnId: string;
  authMethod: 'aadhaar' | 'mobile';
  onProfile: (p: AbhaProfile) => void;
  onXToken?: (t: string) => void;
}) {
  const toast = useToast();
  const { register, getValues, watch, formState: { errors } } = useForm<OtpForm>({ defaultValues: { otp: '' } });
  const otp = watch('otp');

  useEffect(() => {
    ctrl.setNextLabel('Verify');
    ctrl.setNextEnabled(/^\d{6}$/.test(otp));
  }, [otp, ctrl]);

  useEffect(() => {
    ctrl.setNextHandler(async () => {
      const parsed = otpSchema.safeParse(getValues());
      if (!parsed.success) return false;
      try {
        const r = await abha.phrVerifyOtp({ tenantId, txnId, type: authMethod, otp: parsed.data.otp });
        const xToken = r.data?.token ?? r.data?.XToken;
        if (!xToken) {
          toast.error('OTP verification failed', r.message ?? 'No token returned');
          return false;
        }
        onXToken?.(xToken);
        const profileRes = await abha.phrAbhaProfile({ tenantId, XToken: xToken });
        if (!profileRes.data) {
          toast.error('Profile fetch failed', profileRes.message ?? 'No profile returned');
          return false;
        }
        const normalizedPhr = normalizeAbdmProfile(profileRes.data as Record<string, unknown>);
        onProfile(normalizedPhr);
        toast.success('OTP verified', normalizedPhr.name ?? normalizedPhr.abhaAddress ?? undefined);
        return true;
      } catch (e) {
        toast.error('OTP verification failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [getValues, tenantId, txnId, authMethod, onProfile, toast, ctrl]);

  return (
    <Stack spacing={2.5}>
      <Stack spacing={0.5}>
        <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>Enter the OTP</Typography>
        <Typography variant="body2" color="text.secondary">Six-digit code sent by ABDM.</Typography>
      </Stack>
      <TextField
        autoFocus
        label="OTP"
        placeholder="6 digits"
        fullWidth
        inputProps={{ inputMode: 'numeric', maxLength: 6, style: { fontFamily: 'JetBrains Mono, monospace', letterSpacing: 4, fontSize: 20 } }}
        {...register('otp')}
        error={!!errors.otp}
        helperText={errors.otp?.message ?? `Transaction id ${redactMiddle(txnId, 4, 4)}`}
      />
    </Stack>
  );
}

function PhrProfileStep({
  ctrl,
  tenantId,
  profile,
  xToken,
}: {
  ctrl: WizardStepController;
  tenantId: string;
  profile: AbhaProfile | null;
  xToken: string | null;
}) {
  const toast = useToast();
  const [cardBase64, setCardBase64] = useState<string | null>(null);
  const [revisitOpen, setRevisitOpen] = useState(false);
  const [revisitData, setRevisitData] = useState<CheckRevisitResponse | null>(null);
  const revisitResolveRef = useRef<((choice: 'hmis' | 'abdm' | 'cancel') => void) | null>(null);

  useEffect(() => {
    ctrl.setNextLabel('Register Patient');
    ctrl.setNextEnabled(!!profile);
    ctrl.setNextHandler(!profile ? null : async () => {
      const fullName = [profile.firstName, profile.middleName, profile.lastName]
        .filter(Boolean).join(' ').trim() || profile.name || '';
      const saveBody = {
        tenantId,
        abhaAddress: profile.abhaAddress,
        abdmHealthId: profile.abhaNumber,
        mobile: profile.mobile,
        yearOfBirth: profile.yearOfBirth,
        dob: profile.dob,
        gender: profile.gender,
        person: { name: fullName },
        address: typeof profile.address === 'string' ? profile.address : undefined,
        pincode: profile.pincode ?? profile.pinCode,
        district: profile.district,
        state: profile.state,
      };
      try {
        const revisitResp = await abha.checkRevisit(saveBody).catch(() => null);
        if (revisitResp?.isExistingPatient) {
          setRevisitData(revisitResp);
          setRevisitOpen(true);
          const choice = await new Promise<'hmis' | 'abdm' | 'cancel'>((resolve) => {
            revisitResolveRef.current = resolve;
          });
          if (choice === 'cancel') return false;
          if (choice === 'hmis') {
            toast.success('Using existing patient record');
            return true;
          }
        }
        await abha.savePatient(saveBody);
        toast.success('Patient registered', fullName || profile.abhaAddress);
        return true;
      } catch (e) {
        toast.error('Registration failed', (e as Error).message);
        return false;
      }
    });
    return () => ctrl.setNextHandler(null);
  }, [ctrl, profile, tenantId, toast]);

  const fetchCard = async () => {
    if (!xToken) return;
    try {
      const r = await abha.fetchCardByXToken({ tenantId, XToken: xToken });
      const cardData = (r as any)?.data as string | undefined;
      if (cardData) {
        setCardBase64(cardData);
        toast.success('ABHA card fetched');
      } else {
        toast.error('No card returned — ' + ((r as any)?.message ?? 'unknown error'));
      }
    } catch (e) {
      toast.error('Could not fetch card', (e as Error).message);
    }
  };

  const downloadCard = () => {
    if (!cardBase64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${cardBase64}`;
    link.download = `ABHA_Card_${profile?.name ?? 'card'}.png`;
    link.click();
  };

  if (!profile) {
    return <Typography variant="body2" color="text.secondary">No profile yet — go back to OTP.</Typography>;
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h3" sx={{ fontSize: 18, fontWeight: 600 }}>
        {[profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(' ').trim() || profile.name || 'ABHA'}
      </Typography>
      <Box>
        {profile.abhaNumber && <FieldRow label="ABHA number" value={profile.abhaNumber} mono />}
        {profile.abhaAddress && <FieldRow label="ABHA address" value={profile.abhaAddress} mono />}
        {profile.gender && <FieldRow label="Gender" value={profile.gender} />}
        {profile.yearOfBirth && <FieldRow label="Year of birth" value={profile.yearOfBirth} />}
        {profile.mobile && <FieldRow label="Mobile" value={profile.mobile} />}
        {profile.state && <FieldRow label="State" value={profile.state} />}
        {profile.district && <FieldRow label="District" value={profile.district} />}
        {(profile.pincode || profile.pinCode) && (
          <FieldRow label="Pincode" value={profile.pincode ?? profile.pinCode ?? '—'} />
        )}
      </Box>
      {xToken && (
        cardBase64 ? (
          <Box>
            <Typography variant="overline" color="text.tertiary" sx={{ mb: 1, display: 'block' }}>ABHA card</Typography>
            <img src={`data:image/png;base64,${cardBase64}`} alt="ABHA card" style={{ maxWidth: '100%', borderRadius: 8 }} />
            <Button size="small" variant="contained" onClick={downloadCard} sx={{ mt: 1 }}>Download ABHA Card</Button>
          </Box>
        ) : (
          <Button size="small" variant="outlined" onClick={fetchCard}>View &amp; Download ABHA Card</Button>
        )
      )}
      <PatientRevisitDialog
        open={revisitOpen}
        revisitData={revisitData}
        newProfile={profile}
        onChoose={(choice) => { setRevisitOpen(false); revisitResolveRef.current?.(choice); }}
        onClose={() => { setRevisitOpen(false); revisitResolveRef.current?.('cancel'); }}
      />
    </Stack>
  );
}

// ===================================================================
// PatientRevisitDialog — compare HMIS vs ABDM before saving
// ===================================================================

function PatientRevisitDialog({
  open,
  onClose,
  onChoose,
  revisitData,
  newProfile,
}: {
  open: boolean;
  onClose: () => void;
  onChoose: (choice: 'hmis' | 'abdm' | 'cancel') => void;
  revisitData: CheckRevisitResponse | null;
  newProfile: AbhaProfile | null;
}) {
  const theme = useTheme();
  const existing = revisitData?.existingData ?? {};
  const diffs = revisitData?.differences ?? {};
  const hasDiffs = Object.keys(diffs).length > 0;

  const diffFields = [
    { key: 'name',     label: 'Name' },
    { key: 'mobile',   label: 'Mobile' },
    { key: 'gender',   label: 'Gender' },
    { key: 'dob',      label: 'DOB' },
    { key: 'state',    label: 'State' },
    { key: 'district', label: 'District' },
    { key: 'pincode',  label: 'Pincode' },
    { key: 'address',  label: 'Address' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Patient Already Exists
        <Typography variant="caption" color="text.tertiary" sx={{ display: 'block' }}>
          {revisitData?.uhidNumber ? `UHID: ${revisitData.uhidNumber} · ` : ''}
          {hasDiffs ? 'Differences found between HMIS and ABDM data.' : 'No differences found.'}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {hasDiffs ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            {/* HMIS column */}
            <Box>
              <Typography variant="overline" sx={{ color: 'warning.main', fontWeight: 700, display: 'block', mb: 1 }}>
                HMIS Patient Details
              </Typography>
              {diffFields.map(({ key, label }) => {
                const diff = diffs[key];
                return (
                  <Box key={key} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.tertiary">{label}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: diff ? 'error.main' : 'text.primary', fontWeight: diff ? 600 : 400 }}
                    >
                      {diff ? diff.existing : (String(existing[key] ?? '—') || '—')}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* ABDM column */}
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700, display: 'block', mb: 1 }}>
                ABDM Profile Details
              </Typography>
              {diffFields.map(({ key, label }) => {
                const diff = diffs[key];
                const abdmVal = diff?.current ?? (newProfile as Record<string, unknown> | null)?.[key];
                return (
                  <Box key={key} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.tertiary">{label}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: diff ? 'error.main' : 'text.primary', fontWeight: diff ? 600 : 400 }}
                    >
                      {String(abdmVal ?? '—') || '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            This patient already exists with matching data. You can use the existing record or update with ABDM profile.
          </Alert>
        )}
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="outlined" color="warning" onClick={() => onChoose('hmis')}>Use HMIS Data</Button>
        <Button variant="contained" onClick={() => onChoose('abdm')}>Use ABDM Profile</Button>
      </Box>
    </Dialog>
  );
}

// ===================================================================
// PatientEditDialog — edit editable fields of a saved patient
// ===================================================================

function PatientEditDialog({
  open,
  patient,
  tenantId,
  onClose,
  onSaved,
}: {
  open: boolean;
  patient: PatientProfile | null;
  tenantId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theme = useTheme();
  const toast = useToast();
  const [mobile, setMobile] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');
  const [district, setDistrict] = useState('');
  const [state, setState] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (patient) {
      setMobile(patient.mobile ?? '');
      setAddress(typeof patient.address === 'string' ? patient.address : patient.address?.line ?? '');
      setPincode(patient.pincode ?? '');
      setDistrict(patient.district ?? '');
      setState(patient.state ?? '');
    }
  }, [patient]);

  const handleSave = async () => {
    if (!patient) return;
    setSaving(true);
    try {
      await abha.updateProfile({
        id: patient.id,
        tenantId,
        abhaAddress: patient.abhaAddress,
        abdmHealthId: patient.abhaNumber ?? patient.abdmHealthId,
        uhidNumber: patient.uhidNumber,
        mobile: mobile || undefined,
        yearOfBirth: patient.yearOfBirth,
        dob: patient.dob,
        gender: patient.gender,
        person: { name: patient.name ?? patient.person?.name },
        address: address || undefined,
        pincode: pincode || undefined,
        district: district || undefined,
        state: state || undefined,
      });
      toast.success('Patient updated');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Update failed', (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const readonlyProps = { InputProps: { readOnly: true }, sx: { '& .MuiInputBase-input': { color: 'text.secondary' } } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        Edit Patient
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="overline" color="text.tertiary">Personal Info</Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="Name" value={patient?.name ?? patient?.person?.name ?? ''} fullWidth size="small" {...readonlyProps} />
            <TextField label="Gender" value={patient?.gender ?? ''} size="small" sx={{ width: 120 }} InputProps={{ readOnly: true }} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="DOB" value={patient?.dob ?? (patient?.yearOfBirth ? String(patient.yearOfBirth) : '')} size="small" InputProps={{ readOnly: true }} sx={{ width: 160, '& .MuiInputBase-input': { color: 'text.secondary' } }} />
            <TextField
              label="Mobile"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              size="small"
              fullWidth
              inputProps={{ maxLength: 10 }}
            />
          </Stack>

          <Typography variant="overline" color="text.tertiary">ABDM Info</Typography>
          <Stack direction="row" spacing={2}>
            <TextField label="ABHA Address" value={patient?.abhaAddress ?? ''} size="small" fullWidth {...readonlyProps} />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="ABHA Number" value={patient?.abhaNumber ?? patient?.abdmHealthId ?? ''} size="small" fullWidth {...readonlyProps} />
            <TextField label="UHID" value={patient?.uhidNumber ?? ''} size="small" InputProps={{ readOnly: true }} sx={{ width: 160, '& .MuiInputBase-input': { color: 'text.secondary' } }} />
          </Stack>

          <Typography variant="overline" color="text.tertiary">Address</Typography>
          <TextField label="Address" value={address} onChange={(e) => setAddress(e.target.value)} size="small" fullWidth multiline rows={2} />
          <Stack direction="row" spacing={2}>
            <TextField label="Pincode" value={pincode} onChange={(e) => setPincode(e.target.value)} size="small" sx={{ width: 140 }} inputProps={{ maxLength: 6 }} />
            <TextField label="District" value={district} onChange={(e) => setDistrict(e.target.value)} size="small" fullWidth />
            <TextField label="State" value={state} onChange={(e) => setState(e.target.value)} size="small" fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </Box>
    </Dialog>
  );
}

// ===================================================================
// Existing-patient view dialog (clicking a row in the table)
// ===================================================================

function ProfileDialog({ patient, onClose, tenantId, onRefresh }: {
  patient: PatientProfile | null;
  onClose: () => void;
  tenantId: string;
  onRefresh: () => void;
}) {
  const theme = useTheme();
  const [editOpen, setEditOpen] = useState(false);
  return (
    <>
      <Dialog open={!!patient} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
        <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
          {patient?.name ?? patient?.person?.name ?? 'Patient'}
          <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <FieldRow label="Name" value={patient?.name ?? patient?.person?.name ?? '—'} />
          <FieldRow label="ABHA number" value={patient?.abhaNumber ?? '—'} mono />
          <FieldRow label="ABHA address" value={patient?.abhaAddress ?? '—'} mono />
          <FieldRow label="UHID" value={patient?.uhidNumber ?? '—'} mono />
          <FieldRow label="Mobile" value={patient?.mobile ?? '—'} />
          <FieldRow label="Gender" value={patient?.gender ?? '—'} />
          {patient?.yearOfBirth && <FieldRow label="Year of birth" value={patient.yearOfBirth} />}
          {patient?.dob && <FieldRow label="DOB" value={patient.dob} />}
          {patient?.address && (
            <FieldRow
              label="Address"
              value={typeof patient.address === 'string' ? patient.address : patient.address?.line ?? '—'}
            />
          )}
          {patient?.pincode && <FieldRow label="Pincode" value={patient.pincode} />}
          {patient?.district && <FieldRow label="District" value={patient.district} />}
          {patient?.state && <FieldRow label="State" value={patient.state} />}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, borderTop: `1px solid ${theme.palette.border.subtle}` }}>
          <Button variant="outlined" onClick={() => setEditOpen(true)}>Edit</Button>
          <Button onClick={onClose}>Close</Button>
        </Box>
      </Dialog>
      <PatientEditDialog
        open={editOpen}
        patient={patient}
        tenantId={tenantId}
        onClose={() => setEditOpen(false)}
        onSaved={() => { setEditOpen(false); onRefresh(); }}
      />
    </>
  );
}
